import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  TextInput,
  Select,
  Badge,
  Box,
  Tooltip,
  Group,
  Text,
  Textarea,
  OptionsFilter,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { RichTextEditor, Link } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { ReviewBadge } from "./ReviewBadge";
import { rankOptions } from "../../utils/ranking";
import { parseDateOnly, type DateOnlyString } from "../../utils/dateOnly";

// Certainty helper functions
function getCertaintyLabel(certainty: number): string {
  if (certainty >= 95) return "High Certainty";
  if (certainty >= 85) return "Medium Certainty";
  if (certainty >= 75) return "Low Certainty";
  return "Review Required";
}

function getCertaintyBadgeColor(certainty: number): string {
  if (certainty >= 95) return "green";
  if (certainty >= 85) return "yellow";
  if (certainty >= 75) return "orange";
  return "red";
}

// Helper to get actual color hex values for borders
function getCertaintyColorValue(certainty: number): string {
  if (certainty >= 95) return "#40c057"; // green
  if (certainty >= 85) return "#fab005"; // yellow
  if (certainty >= 75) return "#fd7e14"; // orange
  return "#fa5252"; // red
}

interface CertaintyInputProps {
  value?: string;
  onChange?: (value: string) => void;
  certainty?: number;
  onCertaintyChange?: (certainty: number) => void;
  label?: string;
  showReviewBadge?: boolean;
  reviewTooltip?: string;
  [key: string]: any;
}

export function CertaintyTextInput({
  certainty,
  onCertaintyChange,
  label,
  showReviewBadge = false,
  reviewTooltip = "Please verify this field carefully",
  onChange,
  ...props
}: CertaintyInputProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e.currentTarget.value);
      }
      if (onCertaintyChange) {
        onCertaintyChange(100);
      }
    },
    [onCertaintyChange, onChange]
  );

  const hasValue =
    props.value !== undefined && props.value !== null && props.value !== "";
  const showCertainty = certainty !== undefined && hasValue;

  const badge = showCertainty ? (
    <Tooltip label={getCertaintyLabel(certainty)} position="top">
      <Badge
        size="xs"
        color={getCertaintyBadgeColor(certainty)}
        variant="filled"
      >
        {certainty.toFixed(1)}%
      </Badge>
    </Tooltip>
  ) : null;

  const hasBadges = showReviewBadge || !!badge;
  const labelElement =
    label || hasBadges ? (
      <Group gap={6} mb={label ? 4 : 2}>
        {label && (
          <Text size="sm" fw={500}>
            {label}
            {props.required && (
              <Text component="span" c="red" fw={700}>
                {" "}
                *
              </Text>
            )}
          </Text>
        )}
        {showReviewBadge && <ReviewBadge tooltip={reviewTooltip} />}
        {badge}
      </Group>
    ) : null;

  return (
    <Box data-field={props.name}>
      {labelElement}
      <TextInput
        {...props}
        label={undefined}
        onChange={handleChange}
        styles={{
          input: {
            ...(showCertainty && {
              borderLeftWidth: 4,
              borderLeftColor: getCertaintyColorValue(certainty),
            }),
            ...(props.readOnly && {
              backgroundColor: "#f8f9fa",
              color: "#495057",
              fontStyle: "italic",
              opacity: 0.85,
              cursor: "not-allowed",
              borderStyle: "dashed",
            }),
          },
        }}
      />
    </Box>
  );
}

interface CertaintySelectProps {
  value?: string | null;
  onChange?: (value: string | null) => void;
  certainty?: number;
  onCertaintyChange?: (certainty: number) => void;
  label?: string;
  showReviewBadge?: boolean;
  reviewTooltip?: string;
  creatable?: boolean;
  creatableLabel?: string;
  [key: string]: any;
}

export function CertaintySelect({
  certainty,
  onCertaintyChange,
  label,
  showReviewBadge = false,
  reviewTooltip = "Please verify this field carefully",
  creatable = false,
  creatableLabel,
  onChange,
  onSearchChange,
  onFocus,
  onBlur,
  onInput,
  onKeyDown,
  ...props
}: CertaintySelectProps) {
  const [searchValue, setSearchValue] = useState("");
  const searchRef = useRef("");
  const lastTypedSearchRef = useRef("");
  const isPassiveBlurRef = useRef(false);
  const hasChangedSinceFocusRef = useRef(false);
  const isInitialFocusRef = useRef(false);
  const wasClearedRef = useRef(false);

  const currentLabel = useMemo(() => {
    if (!props.value || !props.data) return "";
    const item = props.data.find((d: any) =>
      typeof d === "string" ? d === props.value : d.value === props.value
    );
    if (item) {
      return typeof item === "string" ? item : item?.label || "";
    }
    if (creatable) {
      const isUuid =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          props.value
        );
      if (!isUuid) {
        return props.value;
      }
    }
    return "";
  }, [props.data, props.value, creatable]);

  // Sync searchRef to match the label whenever the value or label changes,
  // ensuring passive navigation guard works correctly.
  useEffect(() => {
    searchRef.current = currentLabel;
    lastTypedSearchRef.current = currentLabel;
    setSearchValue(currentLabel);
  }, [currentLabel]);

  const hasValue =
    props.value !== undefined && props.value !== null && props.value !== "";

  const resetSearchInteraction = useCallback(() => {
    isPassiveBlurRef.current = false;
    hasChangedSinceFocusRef.current = false;
    isInitialFocusRef.current = false;
    wasClearedRef.current = false;
  }, []);

  const handleChange = useCallback(
    (value: string | null) => {
      // C-4762: Prevent passive blur/tab from clearing existing values.
      // We only block a null emission if it is a passive blur and the user
      // has not interacted or explicitly modified/cleared the field since focus.
      const isPassiveBlur =
        value === null &&
        hasValue &&
        isPassiveBlurRef.current &&
        !hasChangedSinceFocusRef.current;

      // C-5044: If we are in creatable mode and the user typed an unmatched value,
      // prevent standard Mantine from resetting/clearing on blur (which emits null).
      const effectiveSearch = wasClearedRef.current
        ? ""
        : lastTypedSearchRef.current || searchRef.current;
      const isCustomCreatableBlur =
        value === null &&
        creatable &&
        effectiveSearch.trim() !== "" &&
        hasChangedSinceFocusRef.current;

      if (isPassiveBlur || isCustomCreatableBlur) {
        return;
      }

      if (value !== null) {
        resetSearchInteraction();
      }
      if (onChange) {
        onChange(value);
      }
      if (onCertaintyChange) {
        onCertaintyChange(100);
      }
    },
    [hasValue, onCertaintyChange, onChange, creatable, resetSearchInteraction]
  );

  const filter: OptionsFilter = useCallback(
    ({ options, search }) => rankOptions(options as any[], search),
    []
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Tab" && props.searchable) {
        // autoSelectOnBlur is false; this fallback covers typed-text+Tab
        // when no option is keyboard-highlighted yet.
        const search = searchRef.current.toLowerCase().trim();
        if (!search) {
          if (onKeyDown) onKeyDown(e);
          return;
        }

        // Only apply text fallback if no option is currently keyboard-selected
        const selectedOption = document.querySelector(
          "[data-combobox-option][data-combobox-selected]"
        );
        if (!selectedOption) {
          const data: any[] = props.data || [];
          const ranked = rankOptions(data, search);
          const match = ranked[0];
          if (match) {
            handleChange(typeof match === "string" ? match : match.value);
          }
        }
      }
      if (onKeyDown) onKeyDown(e);
    },
    [handleChange, props.data, onKeyDown, props.searchable]
  );

  const handleSearchChange = useCallback(
    (val: string) => {
      isPassiveBlurRef.current = false;
      setSearchValue(val);

      // C-4762: Only record a change if it's not the initial focus tick
      // and the value actually differs from the current label.
      // Mantine Select resets internal search on focus, which we ignore.
      if (!isInitialFocusRef.current && val !== currentLabel) {
        hasChangedSinceFocusRef.current = true;

        if (val.trim() !== "") {
          lastTypedSearchRef.current = val;
        } else {
          lastTypedSearchRef.current = "";
        }
      }

      // If we see a value different from the current label, it's no longer initial focus
      if (val !== currentLabel) {
        isInitialFocusRef.current = false;
      }

      searchRef.current = val;
      if (onSearchChange) onSearchChange(val);
    },
    [currentLabel, onSearchChange]
  );

  const handleInput = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      wasClearedRef.current = event.currentTarget.value.trim() === "";
      hasChangedSinceFocusRef.current = true;
      if (onInput) onInput(event);
    },
    [onInput]
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (!hasChangedSinceFocusRef.current) {
        isPassiveBlurRef.current = false;
        isInitialFocusRef.current = true;
        wasClearedRef.current = false;
        searchRef.current = currentLabel;
        lastTypedSearchRef.current = currentLabel;
        setSearchValue(currentLabel);

        // Reset isInitialFocusRef on the next tick for real browser behavior
        setTimeout(() => {
          isInitialFocusRef.current = false;
        }, 0);
      }

      if (onFocus) onFocus(e);
    },
    [currentLabel, onFocus]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Mantine restores the selected label before onBlur, so the native input
      // marker distinguishes an explicit user clear from that internal reset.
      const effectiveSearch = wasClearedRef.current
        ? ""
        : lastTypedSearchRef.current || searchRef.current;
      const isSearchEmpty = effectiveSearch.trim() === "";
      if (
        props.searchable &&
        hasValue &&
        isSearchEmpty &&
        hasChangedSinceFocusRef.current
      ) {
        handleChange(null);
      } else if (
        creatable &&
        props.searchable &&
        !isSearchEmpty &&
        hasChangedSinceFocusRef.current
      ) {
        // C-5044: If creatable is true and we have typed an unmatched value,
        // accept it on blur instead of letting Mantine clear it.
        const s = effectiveSearch.trim();
        const hasExactMatch = (props.data || []).some((d: any) => {
          const label = typeof d === "string" ? d : d.label || d.value;
          return label.toLowerCase() === s.toLowerCase();
        });

        if (!hasExactMatch) {
          handleChange(s);
        }
      }
      if (onBlur) onBlur(e);
    },
    [handleChange, hasValue, onBlur, props.searchable, creatable, props.data]
  );

  const handleKeyDownInternal = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Any keyboard interaction means we're past the initial focus auto-change
      isInitialFocusRef.current = false;

      if (e.key === "Tab") {
        isPassiveBlurRef.current = true;
      } else {
        isPassiveBlurRef.current = false;
      }

      // C-4762: Record manual editing keys to capture explicit clearing
      // Exclude non-editing navigation/modifier keys.
      const nonEditingKeys = [
        "Tab",
        "Shift",
        "Control",
        "Alt",
        "Meta",
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "Enter",
        "Escape",
      ];
      if (!nonEditingKeys.includes(e.key)) {
        hasChangedSinceFocusRef.current = true;
      }

      handleKeyDown(e);
    },
    [handleKeyDown]
  );

  const showCertainty = certainty !== undefined && hasValue;

  const selectData = useMemo(() => {
    const baseData = props.data || [];
    const s = searchValue.trim();
    if (!creatable || !s) return baseData;

    const hasExactMatch = baseData.some((d: any) => {
      const label = typeof d === "string" ? d : d.label || d.value;
      return label.toLowerCase() === s.toLowerCase();
    });

    if (hasExactMatch) return baseData;

    const isCurrentValue =
      props.value && props.value.toLowerCase() === s.toLowerCase();

    return [
      ...baseData,
      {
        value: s,
        label: isCurrentValue
          ? s
          : creatableLabel
          ? `${creatableLabel} "${s}"`
          : `+ Create "${s}"`,
      },
    ];
  }, [props.data, creatable, searchValue, creatableLabel, props.value]);

  const badge = showCertainty ? (
    <Tooltip label={getCertaintyLabel(certainty)} position="top">
      <Badge
        size="xs"
        color={getCertaintyBadgeColor(certainty)}
        variant="filled"
      >
        {certainty.toFixed(1)}%
      </Badge>
    </Tooltip>
  ) : null;

  const hasBadges = showReviewBadge || !!badge;
  const labelElement =
    label || hasBadges ? (
      <Group gap={6} mb={label ? 4 : 2}>
        {label && (
          <Text size="sm" fw={500}>
            {label}
            {props.required && (
              <Text component="span" c="red" fw={700}>
                {" "}
                *
              </Text>
            )}
          </Text>
        )}
        {showReviewBadge && <ReviewBadge tooltip={reviewTooltip} />}
        {badge}
      </Group>
    ) : null;

  return (
    <Box data-field={props.name}>
      {labelElement}
      <Select
        {...props}
        data={selectData}
        label={undefined}
        onChange={handleChange}
        autoSelectOnBlur={false}
        filter={filter}
        onSearchChange={handleSearchChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onInput={handleInput}
        onKeyDown={handleKeyDownInternal}
        nothingFoundMessage={props.nothingFoundMessage || "No matches found"}
        styles={{
          input: {
            ...(showCertainty && {
              borderLeftWidth: 4,
              borderLeftColor: getCertaintyColorValue(certainty),
            }),
            ...(props.readOnly && {
              backgroundColor: "#f8f9fa",
              color: "#495057",
              fontStyle: "italic",
              opacity: 0.85,
              cursor: "not-allowed",
              borderStyle: "dashed",
            }),
          },
        }}
      />
    </Box>
  );
}

interface CertaintyDateInputProps {
  value?: DateOnlyString | null;
  onChange?: (value: DateOnlyString | null) => void;
  certainty?: number;
  onCertaintyChange?: (certainty: number) => void;
  label?: string;
  showReviewBadge?: boolean;
  reviewTooltip?: string;
  [key: string]: any;
}

export function CertaintyDateInput({
  certainty,
  onCertaintyChange,
  label,
  showReviewBadge = false,
  reviewTooltip = "Please verify this field carefully",
  onChange,
  ...props
}: CertaintyDateInputProps) {
  const handleChange = useCallback(
    (value: string | null) => {
      const dateOnly = value === null ? null : parseDateOnly(value);
      if (dateOnly === undefined) return;
      if (onChange) {
        onChange(dateOnly);
      }
      if (onCertaintyChange) {
        onCertaintyChange(100);
      }
    },
    [onCertaintyChange, onChange]
  );

  const hasValue = props.value !== undefined && props.value !== null;
  const showCertainty = certainty !== undefined && hasValue;

  const badge = showCertainty ? (
    <Tooltip label={getCertaintyLabel(certainty)} position="top">
      <Badge
        size="xs"
        color={getCertaintyBadgeColor(certainty)}
        variant="filled"
      >
        {certainty.toFixed(1)}%
      </Badge>
    </Tooltip>
  ) : null;

  const hasBadges = showReviewBadge || !!badge;
  const labelElement =
    label || hasBadges ? (
      <Group gap={6} mb={label ? 4 : 2}>
        {label && (
          <Text size="sm" fw={500}>
            {label}
            {props.required && (
              <Text component="span" c="red" fw={700}>
                {" "}
                *
              </Text>
            )}
          </Text>
        )}
        {showReviewBadge && <ReviewBadge tooltip={reviewTooltip} />}
        {badge}
      </Group>
    ) : null;

  return (
    <Box data-field={props.name}>
      {labelElement}
      <DateInput
        {...props}
        label={undefined}
        onChange={handleChange}
        styles={{
          input: showCertainty
            ? {
                borderLeftWidth: 4,
                borderLeftColor: getCertaintyColorValue(certainty),
              }
            : undefined,
        }}
      />
    </Box>
  );
}

interface CertaintyTextareaProps {
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  certainty?: number;
  onCertaintyChange?: (certainty: number) => void;
  label?: string;
  showReviewBadge?: boolean;
  reviewTooltip?: string;
  [key: string]: any;
}

export function CertaintyTextarea({
  certainty,
  onCertaintyChange,
  label,
  showReviewBadge = false,
  reviewTooltip = "Please verify this field carefully",
  onChange,
  ...props
}: CertaintyTextareaProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(e);
      }
      if (onCertaintyChange) {
        onCertaintyChange(100);
      }
    },
    [onCertaintyChange, onChange]
  );

  const hasValue =
    props.value !== undefined && props.value !== null && props.value !== "";
  const showCertainty = certainty !== undefined && hasValue;

  const badge = showCertainty ? (
    <Tooltip label={getCertaintyLabel(certainty)} position="top">
      <Badge
        size="xs"
        color={getCertaintyBadgeColor(certainty)}
        variant="filled"
      >
        {certainty.toFixed(1)}%
      </Badge>
    </Tooltip>
  ) : null;

  const hasBadges = showReviewBadge || !!badge;
  const labelElement =
    label || hasBadges ? (
      <Group gap={6} mb={label ? 4 : 2}>
        {label && (
          <Text size="sm" fw={500}>
            {label}
            {props.required && (
              <Text component="span" c="red" fw={700}>
                {" "}
                *
              </Text>
            )}
          </Text>
        )}
        {showReviewBadge && <ReviewBadge tooltip={reviewTooltip} />}
        {badge}
      </Group>
    ) : null;

  return (
    <Box data-field={props.name}>
      {labelElement}
      <Textarea
        {...props}
        label={undefined}
        onChange={handleChange}
        styles={{
          input: {
            ...(showCertainty && {
              borderLeftWidth: 4,
              borderLeftColor: getCertaintyColorValue(certainty),
            }),
            ...(props.readOnly && {
              backgroundColor: "#f8f9fa",
              color: "#495057",
              fontStyle: "italic",
              opacity: 0.85,
              cursor: "not-allowed",
              borderStyle: "dashed",
            }),
          },
        }}
      />
    </Box>
  );
}

interface CertaintyRichTextProps {
  value?: string;
  onChange?: (value: string) => void;
  certainty?: number;
  onCertaintyChange?: (certainty: number) => void;
  label?: string;
  showReviewBadge?: boolean;
  reviewTooltip?: string;
  placeholder?: string;
  readOnly?: boolean;
  name?: string;
}

export function CertaintyRichText({
  certainty,
  onCertaintyChange,
  label,
  showReviewBadge = false,
  reviewTooltip = "Please verify this field carefully",
  value = "",
  onChange,
  placeholder = "Enter text...",
  readOnly = false,
  name,
}: CertaintyRichTextProps) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const isUpdatingRef = useRef(false);
  const lastValueRef = useRef(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Placeholder.configure({ placeholder }),
      Underline,
      Link,
    ],
    content: value || "",
    editable: !readOnly,
    onUpdate: ({ editor: ed }) => {
      if (isUpdatingRef.current) return;
      const html = ed.getHTML();
      if (html !== lastValueRef.current) {
        lastValueRef.current = html;
        onChangeRef.current?.(html);
        if (onCertaintyChange) onCertaintyChange(100);
      }
    },
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (value !== lastValueRef.current) {
      isUpdatingRef.current = true;
      editor.commands.setContent(value || "");
      lastValueRef.current = value;
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    }
  }, [value, editor]);

  const hasValue = value !== undefined && value !== null && value !== "";
  const showCertainty = certainty !== undefined && hasValue;

  const badge = showCertainty ? (
    <Tooltip label={getCertaintyLabel(certainty)} position="top">
      <Badge
        size="xs"
        color={getCertaintyBadgeColor(certainty)}
        variant="filled"
      >
        {certainty.toFixed(1)}%
      </Badge>
    </Tooltip>
  ) : null;

  const hasBadges = showReviewBadge || !!badge;
  const labelElement =
    label || hasBadges ? (
      <Group gap={6} mb={label ? 4 : 2}>
        {label && (
          <Text size="sm" fw={500}>
            {label}
          </Text>
        )}
        {showReviewBadge && <ReviewBadge tooltip={reviewTooltip} />}
        {badge}
      </Group>
    ) : null;

  return (
    <Box data-field={name}>
      {labelElement}
      <Box
        style={{
          ...(showCertainty && {
            borderLeft: `4px solid ${getCertaintyColorValue(certainty)}`,
            borderRadius: 4,
          }),
        }}
      >
        <RichTextEditor editor={editor}>
          <RichTextEditor.Toolbar sticky stickyOffset={0}>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Bold />
              <RichTextEditor.Italic />
              <RichTextEditor.Underline />
              <RichTextEditor.Strikethrough />
            </RichTextEditor.ControlsGroup>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.BulletList />
              <RichTextEditor.OrderedList />
            </RichTextEditor.ControlsGroup>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Link />
              <RichTextEditor.Unlink />
            </RichTextEditor.ControlsGroup>
          </RichTextEditor.Toolbar>
          <RichTextEditor.Content />
        </RichTextEditor>
      </Box>
    </Box>
  );
}
